import { ChangeEvent, DragEvent, useRef, useState } from "react";
import { ImageUploadProps } from "./types";

export const ACCEPTED = ["image/jpeg", "image/png", "image/webp", "image/svg+xml"];
export const MAX_MB = 5;

export function ImageUpload({ preview, error, onFile, onClear }: ImageUploadProps) {
    const [dragging, setDragging] = useState(false);
    const [fileError, setFileError] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const validate = (file: File): string | null => {
        if (!ACCEPTED.includes(file.type)) return "Only JPG, PNG, WebP or SVG files are accepted.";
        if (file.size > MAX_MB * 1024 * 1024) return `File exceeds ${MAX_MB} MB limit.`;
        return null;
    };

    const handleFile = (file: File) => {
        const err = validate(file);
        if (err) { setFileError(err); return; }
        setFileError(null);
        onFile(file);
    };

    const onDrop = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault(); setDragging(false);
        const file = e.dataTransfer.files[0];
        if (file) handleFile(file);
    };

    const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) handleFile(file);
        e.target.value = "";
    };

    const combinedError = fileError || error;

    if (preview) {
        return (
            <div style={{ position: "relative", display: "inline-block" }}>
                <div style={{
                    width: "100%", borderRadius: 10, overflow: "hidden",
                    border: "1px solid var(--border)", background: "var(--input-bg)",
                    position: "relative",
                }}>
                    <img
                        src={preview} alt="Certification preview"
                        style={{ width: "100%", maxHeight: 220, objectFit: "contain", display: "block", padding: 12 }}
                    />
                    {/* Overlay actions */}
                    <div
                        // style={{
                        //     position: "absolute", inset: 0, background: "rgba(0,0,0,0)",
                        //     display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                        //     opacity: 0, transition: "opacity 0.2s",
                        // }}
                        onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
                        onMouseLeave={(e) => (e.currentTarget.style.opacity = "0")}
                        style={{
                            position: "absolute", inset: 0, background: "rgba(0,0,0,0.55)",
                            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                            opacity: 0, transition: "opacity 0.2s",
                        }}
                    >
                        <button
                            onClick={() => inputRef.current?.click()}
                            style={{
                                padding: "8px 16px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.3)",
                                background: "rgba(255,255,255,0.1)", color: "#fff", fontSize: 13, cursor: "pointer",
                                fontFamily: "'DM Sans', sans-serif",
                            }}
                        >Replace</button>
                        <button
                            onClick={onClear}
                            style={{
                                padding: "8px 16px", borderRadius: 8, border: "1px solid rgba(224,82,82,0.4)",
                                background: "rgba(224,82,82,0.15)", color: "#e05252", fontSize: 13, cursor: "pointer",
                                fontFamily: "'DM Sans', sans-serif",
                            }}
                        >Remove</button>
                    </div>
                </div>
                <div style={{ fontSize: 12, color: "var(--text3)", marginTop: 6 }}>
                    Hover the image to replace or remove it.
                </div>
                <input ref={inputRef} type="file" accept={ACCEPTED.join(",")} onChange={onInputChange} style={{ display: "none" }} />
            </div>
        );
    }

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <div
                onClick={() => inputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={onDrop}
                style={{
                    border: `2px dashed ${combinedError ? "rgba(224,82,82,0.5)" : dragging ? "var(--or)" : "rgba(255,255,255,0.12)"}`,
                    borderRadius: 12, padding: "36px 24px",
                    background: dragging ? "var(--or-dim)" : combinedError ? "rgba(224,82,82,0.04)" : "var(--input-bg)",
                    cursor: "pointer", textAlign: "center",
                    transition: "all 0.2s",
                }}
            >
                {/* Upload icon */}
                <div style={{
                    width: 48, height: 48, borderRadius: "50%", margin: "0 auto 14px",
                    background: dragging ? "var(--or-dim)" : "rgba(255,255,255,0.05)",
                    border: `1px solid ${dragging ? "rgba(191,61,12,0.3)" : "var(--border)"}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    transition: "all 0.2s",
                }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                        stroke={dragging ? "var(--or)" : "var(--text3)"} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="17 8 12 3 7 8" />
                        <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                </div>
                <div style={{ fontSize: 14, fontWeight: 500, color: dragging ? "var(--or)" : "var(--text2)", marginBottom: 4 }}>
                    {dragging ? "Drop to upload" : "Drop image here or click to browse"}
                </div>
                <div style={{ fontSize: 12, color: "var(--text3)" }}>
                    JPG, PNG, WebP, SVG · Max {MAX_MB} MB
                </div>
            </div>
            {combinedError && <span style={{ fontSize: 12, color: "#e05252" }}>{combinedError}</span>}
            <input ref={inputRef} type="file" accept={ACCEPTED.join(",")} onChange={onInputChange} style={{ display: "none" }} />
        </div>
    );
}
